import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs
} from "@mui/material";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import AppButton from "../../../../AppComponents/AppButton";
import { AppMaterialIcons } from "../../../../AppComponents/AppMaterialIcons";
import {
  HocBackdropLoader,
  HocBackdropLoaderProps,
} from "../../../../HOC/HocBackdropLoader";
import useDebounce from "../../../../Hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../../../../Store";

import { useInfiniteQuery } from "@tanstack/react-query";
import { ProtectedAppUiElement } from "../../../../AppComponents/ProtectedComponents";
import getPublicAssets from "../../../../Services/s3bucket";
import { useAuthStore } from "../../../../Store/Slices/AuthSlice";
import {
  // INTIAL_ACTIVE_FILTERS,
  TEMP_ACTIVE_FILTERS,
  setActiveChatTab,
  setChatSessionActiveFilters,
  setPageNumber,
} from "../../../../Store/Slices/socket/liveChat/chat.slice";
import { ChatNetworkService } from "../../../../Store/Slices/socket/liveChat/ChatNetworkService";
import { debounce, getDefaultAvatar } from "../../../../Utils";
import { queryClient } from "../../../../Utils/tanstack-query";
import { getHtmlTextChannelWise } from "../../../appComponents/Forms/RichEditor/WebLiveChat/utils";
import { useSocket } from "../../../hooks/socket-context";
import { StyledAppInput } from "../../../Styles/StyledForms";
import {
  colorPairs,
  getChatAppliedFiltersCount,
  getChatSessionFilterPayloadData,
  getCurrentChatTabId,
} from "../../../utils/utils";
import {
  AllChatPannelWrapper,
  StyleTabHeaderWrapper,
  StyledCircularLoaderBox,
  TabFilter,
  TopBar,
} from "./AllchatPannelStyle";
import ChatCard from "./ChatCard/ChatCard";
import ChatFiltersScreen from "./ChatFiltersScreen";
import { chatFilterStatusOptions } from "./constants";
import SelectActions from "./SelectActions/SelectActions";

export let audio: any = new Audio(getPublicAssets("audio/tong.mp3"));

export const playSoundLastBotMsg = () => {
  audio.volume = 1;
  audio.play();
};

interface Props extends HocBackdropLoaderProps {}

const AllchatPannel: React.FC<Props> = ({
  setLoading: setBackdropLoading,
  isLoading: isBackdropLoading,
}) => {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(
    (state: any) => state.currentAllMessagesStore.sessionId
  );
  const chatSessionActiveFilter = useAppSelector(
    (state) => state.chat.data.chatSessionActiveFilter
  );
  const bulkSelectedSessions = useAppSelector(
    (state) => state.chat.data.bulkSelectedSessions
  );
  const activeChatTab = useAppSelector(
    (state) => state.chat.data.activeChatTab
  );

  const filters = useMemo(
    () => getChatSessionFilterPayloadData(chatSessionActiveFilter),
    [chatSessionActiveFilter]
  );
  const selectData = useCallback(
    (data: any) => {
      const isRecent = filters?.sortby === "recent"; // Check if sorting by 'recent'
      // console.log(`🚀 ~ data:`, data);
      const sessions: any[] = data.pages
        .flatMap((page: any) => page?.data?.data ?? [])

        .sort((a: any, b: any) => {
          const dateA = new Date(a?.date ?? a?.createdAt).getTime();
          const dateB = new Date(b?.date ?? b?.createdAt).getTime();

          // Sort based on the `isRecent` condition
          return isRecent
            ? dateB - dateA // Descending (recent first)
            : dateA - dateB; // Ascending (oldest first)
        });

      return sessions;
    },
    [filters]
  );

  // 🟢 Using React Query's caching efficiently,
  const {
    data,
    isLoading,
    fetchNextPage,
    // refetch,
    // isRefetching,
    hasNextPage,
    // isFetchNextPageError,
    // isFetched,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["conversations", "sessions", "listing", filters],
    queryFn: ({ pageParam }) => ChatNetworkService.sessionsListing(pageParam),
    initialPageParam: { ...filters, page: 0 },
    getNextPageParam: (lastPage: any, pages: any[]) => {
      if (lastPage?.data?.data?.length === 0) return undefined;
      const page = pages?.length + 1;
      return { ...filters, page };
    },
    getPreviousPageParam: (lastPage: any, pages: any[]) => {
      const page = pages?.length - 1 > 1 ? pages?.length - 1 : 1;
      return { ...filters, page };
    },
    retry: 1,
    staleTime: 0,
    _optimisticResults: "optimistic",
    select: selectData,
    meta:{ persist: true},
    placeholderData: () => {
      const cached = queryClient.getQueryData<any>([
        "conversations",
        "sessions",
        "listing",
        filters,
      ]);
      return cached ?? null;
    },
  });

  // 🟢 Debounced nextfetch to avoid rapid multiple fetches during fast scroll
  const debouncedFetchNextPage = debounce(fetchNextPage, 300);

  // Memoized data
  const allSessionsData :any = useMemo(() => data ?? [], [data]);

  // console.log(`🚀 ~ allSessionsData:`, allSessionsData);

  const updateInboxSession = useCallback(() => {
    const key2 = ["conversations", "sessions", "listing", filters];
    queryClient.invalidateQueries({ queryKey: key2, exact: true });
  }, [filters]);

  // const { socket } = useSocket();
  const { userConfig } = useAppSelector(useAuthStore);

  // const debouncedSearchTerm = useDebounce<string>(
  //   chatSessionActiveFilter?.search || "",
  //   1000
  // );

  // const [isShowMoreBtnShowing, setIsShowMoreBtnShowing] =
  //   useState<boolean>(true);
  const [isFilterScreenOpen, setIsFilterScreenOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  // Create a ref to hold the input element
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {

    dispatch(setActiveChatTab(newValue));
    // setActiveTabIndex(newValue);
    dispatch(setPageNumber(0));

    const id: string =
      getCurrentChatTabId(
        newValue,
        userConfig?.payload?.allowed_permissions?.can_view_all_chats_tab ===
          true
      ) || "mine";
    
    let payload = {};
    if (id === "unassigned") {
      payload = {
        conversation: "",
        conversation_status: [{ value: "unassigned", label: "Unassigned" }],
        chat_status: [{ value: "Open", label: "Open" }], // default to Open
      };
    } else {
      // if (id === "mine" || id === "all") {
      payload = {
        conversation: id,
        conversation_status: [],
        chat_status: [{ value: "Open", label: "Open" }], // default to Open
      };
    }
    const updatedFilters = { ...TEMP_ACTIVE_FILTERS, ...payload };
    dispatch(setChatSessionActiveFilters(updatedFilters));

  };


  const handleLoadMoreButtton = () => {
    debouncedFetchNextPage();
  };



  useEffect(() => {
    if (!userConfig?.payload) return;
    const isAllTabsAvailable =
      userConfig?.payload?.allowed_permissions?.can_view_all_chats_tab === true;

    if (
      !isAllTabsAvailable &&
      chatSessionActiveFilter?.conversation === "all"
    ) {
      const initialFilter = {
        ...TEMP_ACTIVE_FILTERS,
        conversation: "mine",
      };
      dispatch(setChatSessionActiveFilters(initialFilter));
    }
  }, [userConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chatSessionActiveFilter) return;
    if (!userConfig?.payload) return;

    const isAllTabsAvailable =
      userConfig?.payload?.allowed_permissions?.can_view_all_chats_tab === true;

    let index: number = 0;

    if (chatSessionActiveFilter?.conversation === "all") {
      index = 0;
    }

    if (chatSessionActiveFilter?.conversation === "mine") {
      index = isAllTabsAvailable ? 1 : 0;
    }
    if (
      chatSessionActiveFilter?.conversation_status?.find(
        (item: any) => item?.value === "unassigned"
      )
    ) {
      index = isAllTabsAvailable ? 2 : 1; // Unassigned tab
    }

    dispatch(setActiveChatTab(index));
  }, [chatSessionActiveFilter]); // eslint-disable-line react-hooks/exhaustive-deps


  // Define the debounced function
  const handleInputChange = useCallback(
    debounce((value: string) => {
      // console.log("Debounced Input Value:", value);
      // setActiveTabIndex(0); // Reset to the first tab
      dispatch(setActiveChatTab(0));
      const isAllTabsAvailable =
      userConfig?.payload?.allowed_permissions?.can_view_all_chats_tab === true;
      dispatch(
        setChatSessionActiveFilters({
          ...TEMP_ACTIVE_FILTERS,
          conversation: value ? "" : isAllTabsAvailable ? "all" : "mine",
          search: value,
          chat_status: value ? [] : [chatFilterStatusOptions?.[0]]
        })
      );

      if (!value) {
        setSearchText(""); // Clear local state if input is empty
      }
    }, 1000),
    []
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle input change
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value ?? "";
    setSearchText(value); // Update local state for immediate UI feedback
    handleInputChange(value.trim()); // ✅ correct // Pass the input value to the debounced function
  };

  const handleSearchChange = (e: any) => {
    let searchTextChat: string = e?.target?.value || "";
    let payload = { search: searchTextChat, conversation: "" };

    if (typeof payload !== "object") return;

    if (searchTextChat?.length > 0) {
      dispatch(setChatSessionActiveFilters(payload));
    } else if (searchTextChat?.length === 0) {
      dispatch(setChatSessionActiveFilters(TEMP_ACTIVE_FILTERS));
    }
  };


  return (
    <AllChatPannelWrapper>
      {isFilterScreenOpen ? (
        <Box sx={{ position: "relative", height: "calc(100vh - 64px)" }}>
          <ChatFiltersScreen setIsFilterScreenOpen={setIsFilterScreenOpen} />
        </Box>
      ) : (
        <Fragment>
          <TopBar>
            {/* <div className="icon"></div> */}
            <div className="search">
              <StyledAppInput
                ref={inputRef}
                type="search"
                value={searchText}
                // value={chatSessionActiveFilter?.search || ""}
                onChange={(onChange)}
                name="search"
                id="inbox_search"
                placeholder="Search"
                // style={{ backgroudColor: theme.palette.secondary.main }}
              />
            </div>
          </TopBar>
          {bulkSelectedSessions?.length > 0 ? (
            <SelectActions />
          ) : (
            <TabFilter>
              <Box sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={activeChatTab ?? 0}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    {userConfig?.payload?.allowed_permissions
                      ?.can_view_all_chats_tab === true ? (
                      <Tab
                        style={{ flex: 1, fontSize: "13px" }}
                        label={CustomTabHeader("All", 0)}
                        {...a11yProps(0)}
                        disableRipple
                      />
                    ) : null}
                    <Tab
                      style={{ flex: 1, fontSize: "13px" }}
                      label={CustomTabHeader("You", 0)}
                      {...a11yProps(1)}
                      disableRipple
                    />
                    <Tab
                      style={{ flex: 1, fontSize: "13px" }}
                      label={CustomTabHeader("Unassigned", 0)}
                      {...a11yProps(2)}
                      disableRipple
                    />
                  </Tabs>
                </Box>
              </Box>
              <ProtectedAppUiElement moduleId="can_apply_filters">
                <IconButton
                  className="filter"
                  id="filter-icon-inbox"
                  onClick={() => setIsFilterScreenOpen(!isFilterScreenOpen)}
                >
                  <Badge
                    badgeContent={getChatAppliedFiltersCount(
                      chatSessionActiveFilter
                    )}
                    variant="dot"
                    max={5}
                    color="primary"
                    showZero={false}
                  >
                    <AppMaterialIcons
                      iconName="FilterList"
                      style={{ fontSize: "22px" }}
                    />
                  </Badge>
                </IconButton>
              </ProtectedAppUiElement>
            </TabFilter>
          )}
          {isLoading ? (
            <CustomSpinnerLoader
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
              size={28}
            />
          ) : null}

          <div className="chat-window-content">
            {allSessionsData && allSessionsData?.length > 0 ? (
              allSessionsData?.map((data: any, index: number) => {
                // let itemColor = index  ;
                const colorPair = colorPairs[index % colorPairs.length]; // Calculate the color pair
                return (
                  <ChatCard
                    session_id={data?.session_id}
                    avatar={data?.avatar}
                    avatarName={getDefaultAvatar(
                      data?.name?.toString() ?? data?.user?.toString() ?? "A"
                    )}
                    avatarColor={colorPair}
                    name={data?.name ?? data?.user ?? "Anonymous"}
                    channel={data?.channel || "website"}
                    channelName={
                      data?.channel_name ?? data?.channel ?? "website"
                    }
                    status={data?.conversation_status || "Open"}
                    description={data?.session_id}
                    date={data?.date ?? data?.last_message_time ?? data?.created_at ?? data?.createdAt}
                    unreadCount={
                      data?.session_id === sessionId ? 0 : data?.count
                    }
                    customTag={data?.labels ?? data?.label ?? []}
                    key={data?.session_id ?? index.toString()}
                    item={data}
                    botType={data?.conversation_type ?? "bot"}
                    agentName={data?.agent_info?.name}
                    last_message={
                      data?.last_message
                        ? typeof data?.last_message === "string"
                          ? getHtmlTextChannelWise(
                              data?.last_message,
                              data?.channel_name ?? "whatsapp"
                            )
                          : "🖼️ Media"
                        : ""
                    }
                  />
                );
              })
            ) : isBackdropLoading ? null : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                No Conversations to show
              </Box>
            )}
            {isFetching && allSessionsData &&
            allSessionsData?.length > 8 ? (
              <StyledCircularLoaderBox>
                <CircularProgress size={20} disableShrink/>
              </StyledCircularLoaderBox>
            ) : null}
            {isBackdropLoading &&
            allSessionsData &&
            allSessionsData?.length > 8 ? (
              <StyledCircularLoaderBox>
                <CircularProgress size={24} />
                <p>loading your conversations...</p>
              </StyledCircularLoaderBox>
            ) : hasNextPage &&
              allSessionsData &&
              allSessionsData?.length > 8 ? (
              <AppButton
                variant="primary"
                style={{ margin: "1rem auto", width: "80%" }}
                onClick={() => handleLoadMoreButtton()}
              >
                Load more
              </AppButton>
            ) : null}
          </div>
        </Fragment>
      )}
    </AllChatPannelWrapper>
  );
};

export default HocBackdropLoader(AllchatPannel);

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CustomTabHeader = (label: string, count: number = 0) => {
  let unReadCount: string | number = count;
  if (count > 99) {
    unReadCount = "99+";
  }
  return (
    <StyleTabHeaderWrapper>
      {label}
      {count > 0 ? <span className="countBox">{unReadCount}</span> : null}
    </StyleTabHeaderWrapper>
  );
};

const CustomSpinnerLoader = ({ style = {}, size = 28 }: any) => {
  return (
    <Box style={style}>
      <CircularProgress size={size} disableShrink />
    </Box>
  );
};
